import { useDisclosure } from '@mantine/hooks'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  OverviewGroupMatch,
  findOverviewGroup
} from '@/components/deployments/deployment-list/DeploymentTree/deploymentTreeHelpers'
import {
  DeploymentGroup,
  DeploymentGroupMember,
  DeploymentOverviewApp
} from '@/types/deploymentGroup'
import { DeploymentGroupDrawerTab } from './DeploymentGroupDrawer'

type DrawerTarget = {
  groupId: string
  tab: DeploymentGroupDrawerTab
  deploymentId?: string
}

export const useDeploymentGroupDrawer = (apps: DeploymentOverviewApp[]) => {
  const [target, setTarget] = useState<DrawerTarget | null>(null)
  const [opened, { open, close }] = useDisclosure(false)

  const match = useMemo(
    () => (target ? findOverviewGroup(apps, target.groupId) : null),
    [apps, target]
  )

  const [lastMatch, setLastMatch] = useState<OverviewGroupMatch | null>(null)
  if (match && match !== lastMatch) {
    setLastMatch(match)
  }
  // The drawer renders the last known group while it slides out after the group is removed.
  const shown = match ?? lastMatch

  useEffect(() => {
    if (opened && !match) {
      close()
    }
  }, [opened, match, close])

  const openGroup = useCallback(
    (
      group: DeploymentGroup,
      member?: DeploymentGroupMember,
      tab?: DeploymentGroupDrawerTab
    ) => {
      setTarget({
        groupId: group.id,
        tab: tab ?? (member ? 'cameras' : 'overview'),
        deploymentId: member?.deployment_id
      })
      open()
    },
    [open]
  )

  const selection =
    shown && target
      ? { ...shown, tab: target.tab, deploymentId: target.deploymentId }
      : null

  return { opened, selection, openGroup, close }
}
